import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, BaseModelObject, ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumVesselMovementSourceType } from "./enums/enumVesselMovementSourceType.generated";
import { EnumVesselMovementTargetType } from "./enums/enumVesselMovementTargetType.generated";

// Model Objects

export class FromVesselDetails extends ModelObject<FromVesselDetails> {

    private _sourceAssemblyId: Guid | null = null;
    public get sourceAssemblyId(): Guid | null { return this._sourceAssemblyId }
    public set sourceAssemblyId(value: Guid | null) {
      this._sourceAssemblyId = value === undefined ? null : value;
    }
    private _sourceProcessId: Guid | null = null;
    public get sourceProcessId(): Guid | null { return this._sourceProcessId }
    public set sourceProcessId(value: Guid | null) {
      this._sourceProcessId = value === undefined ? null : value;
    }
    public sourceVesselId: Guid = Guid.createEmpty();
    public sourceLitresLeaving: number = 0;
    private _sourceFinalLitres: number | null = null;
    /**
    * Litres which remain in the source vessel, if measured. Optional even if SourceType=Vessel.
    */
    public get sourceFinalLitres(): number | null { return this._sourceFinalLitres }
    public set sourceFinalLitres(value: number | null) {
      this._sourceFinalLitres = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.sourceAssemblyId = jsonSerializedObject.sourceAssemblyId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.sourceAssemblyId);
          this.sourceProcessId = jsonSerializedObject.sourceProcessId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.sourceProcessId);
          this.sourceVesselId = Guid.parseOrEmpty(jsonSerializedObject.sourceVesselId);
          this.sourceLitresLeaving = jsonSerializedObject.sourceLitresLeaving;
          this.sourceFinalLitres = jsonSerializedObject.sourceFinalLitres == null ? null : jsonSerializedObject.sourceFinalLitres;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.sourceAssemblyId = this.sourceAssemblyId == null ? null : this.sourceAssemblyId.toString();
        toRet.sourceProcessId = this.sourceProcessId == null ? null : this.sourceProcessId.toString();
        toRet.sourceVesselId = this.sourceVesselId.toString();
        toRet.sourceLitresLeaving = this.sourceLitresLeaving;
        toRet.sourceFinalLitres = this.sourceFinalLitres == null ? null : this.sourceFinalLitres;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "sourceAssemblyId", rule: (v: any) => v == null || !v.isEmpty() || "FromVesselDetails.SourceAssemblyId cannot be empty"},
            {field: "sourceProcessId", rule: (v: any) => v == null || !v.isEmpty() || "FromVesselDetails.SourceProcessId cannot be empty"},
            {field: "sourceVesselId", rule: (v: any) => !v.isEmpty() || "FromVesselDetails.SourceVesselId cannot be empty"},
    ];
}


export class ToVesselDetails extends ModelObject<ToVesselDetails> {

    private _targetAssemblyId: Guid | null = null;
    public get targetAssemblyId(): Guid | null { return this._targetAssemblyId }
    public set targetAssemblyId(value: Guid | null) {
      this._targetAssemblyId = value === undefined ? null : value;
    }
    public targetVesselId: Guid = Guid.createEmpty();
    public targetLitresArriving: number = 0;
    private _targetFinalLitres: number | null = null;
    /**
    * Litres which remain in the target vessel, if measured. Optional even if TargetType=Vessel.
    */
    public get targetFinalLitres(): number | null { return this._targetFinalLitres }
    public set targetFinalLitres(value: number | null) {
      this._targetFinalLitres = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.targetAssemblyId = jsonSerializedObject.targetAssemblyId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.targetAssemblyId);
          this.targetVesselId = Guid.parseOrEmpty(jsonSerializedObject.targetVesselId);
          this.targetLitresArriving = jsonSerializedObject.targetLitresArriving;
          this.targetFinalLitres = jsonSerializedObject.targetFinalLitres == null ? null : jsonSerializedObject.targetFinalLitres;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.targetAssemblyId = this.targetAssemblyId == null ? null : this.targetAssemblyId.toString();
        toRet.targetVesselId = this.targetVesselId.toString();
        toRet.targetLitresArriving = this.targetLitresArriving;
        toRet.targetFinalLitres = this.targetFinalLitres == null ? null : this.targetFinalLitres;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "targetAssemblyId", rule: (v: any) => v == null || !v.isEmpty() || "ToVesselDetails.TargetAssemblyId cannot be empty"},
            {field: "targetVesselId", rule: (v: any) => !v.isEmpty() || "ToVesselDetails.TargetVesselId cannot be empty"},
    ];
}


export class VesselMovementReadOnly extends ModelObject<VesselMovementReadOnly> {

    private _createdBy: Guid | null = null;
    /**
    * User who created this vessel movement. Captured by system not user editable.
    */
    public get createdBy(): Guid | null { return this._createdBy }
    public set createdBy(value: Guid | null) {
      this._createdBy = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.createdBy = jsonSerializedObject.createdBy == null ? null : Guid.parseOrEmpty(jsonSerializedObject.createdBy);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.createdBy = this.createdBy == null ? null : this.createdBy.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "createdBy", rule: (v: any) => v == null || !v.isEmpty() || "VesselMovementReadOnly.CreatedBy cannot be empty"},
    ];
}


export class VesselMovement extends ModelObject<VesselMovement> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Notes
    * Not Nullable
    * Max Length : 100000
    */
    public notes: string = "";
    /**
    * Source Type of movement. Either SourceType or TargetType or Both must be Vessel. : 0 = Ingredients, 1 = Vessel, 2 = Measurement
    */
    public sourceType: EnumVesselMovementSourceType = EnumVesselMovementSourceType.ingredients;
    /**
    * Target Type of movement. Either SourceType or TargetType or Both must be Vessel. : 0 = Drain, 1 = Vessel, 2 = Output
    */
    public targetType: EnumVesselMovementTargetType = EnumVesselMovementTargetType.drain;
    /**
    * Timestamp at which the movement occurred.
    */
    public timestamp: Instant = Instant.ofEpochMilli(0);
    private _targetProcessId: Guid | null = null;
    /**
    * Id of Target Process if this movement went to a vessel. Required if TargetType=Vessel.
    */
    public get targetProcessId(): Guid | null { return this._targetProcessId }
    public set targetProcessId(value: Guid | null) {
      this._targetProcessId = value === undefined ? null : value;
    }
    public readOnly: VesselMovementReadOnly = new VesselMovementReadOnly();
    private _fromVesselDetails: FromVesselDetails | null = null;
    /**
    * Details of the liquid leaving vessel. Required if SourceType=Vessel.
    */
    public get fromVesselDetails(): FromVesselDetails | null { return this._fromVesselDetails }
    public set fromVesselDetails(value: FromVesselDetails | null) {
      this._fromVesselDetails = value === undefined ? null : value;
    }
    private _toVesselDetails: ToVesselDetails | null = null;
    /**
    * Details of the liquid arriving vessel. Required if TargetType=Vessel.
    */
    public get toVesselDetails(): ToVesselDetails | null { return this._toVesselDetails }
    public set toVesselDetails(value: ToVesselDetails | null) {
      this._toVesselDetails = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.notes = jsonSerializedObject.notes;
          this.sourceType = EnumVesselMovementSourceType.getByValue(jsonSerializedObject.sourceType, this.sourceType);
          this.targetType = EnumVesselMovementTargetType.getByValue(jsonSerializedObject.targetType, this.targetType);
          this.timestamp = Instant.parse(jsonSerializedObject.timestamp);
          this.targetProcessId = jsonSerializedObject.targetProcessId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.targetProcessId);
          this.readOnly = new VesselMovementReadOnly(jsonSerializedObject.readOnly);
          this.fromVesselDetails = jsonSerializedObject.fromVesselDetails == null ? null : new FromVesselDetails(jsonSerializedObject.fromVesselDetails);
          this.toVesselDetails = jsonSerializedObject.toVesselDetails == null ? null : new ToVesselDetails(jsonSerializedObject.toVesselDetails);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.notes = this.notes;
        toRet.sourceType = this.sourceType.value;
        toRet.targetType = this.targetType.value;
        toRet.timestamp = this.timestamp.toString();
        toRet.targetProcessId = this.targetProcessId == null ? null : this.targetProcessId.toString();
        toRet.readOnly = this.readOnly.toJsonSerializedObject();
        toRet.fromVesselDetails = this.fromVesselDetails == null ? null : this.fromVesselDetails?.toJsonSerializedObject() ?? null;
        toRet.toVesselDetails = this.toVesselDetails == null ? null : this.toVesselDetails?.toJsonSerializedObject() ?? null;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "VesselMovement.Id cannot be empty"},
            {field: "notes", rule: (v: any) => v == null || v.length <= 100000 || "Notes has too many characters (100000)"},
            {field: "targetProcessId", rule: (v: any) => v == null || !v.isEmpty() || "VesselMovement.TargetProcessId cannot be empty"},
            {field: "readOnly", rule: (v: any) => !!v || "readOnly is required"},
            {field: "fromVesselDetails", rule: (v: any) => (v === null) || v.validate()},
            {field: "toVesselDetails", rule: (v: any) => (v === null) || v.validate()},
    ];
}


export class VesselMovementWithFinal extends ModelObject<VesselMovementWithFinal> {

    public vesselMovement: VesselMovement = new VesselMovement();
    private _sourceExpectedFinalLitres: number | null = null;
    /**
    * The expected volume in the source vessel after the movement, if source is a vessel in the current assembly. Evaluated from the final litres (measured or expected) of the previous movement for the vessel and the SourceLitresLeaving.
    */
    public get sourceExpectedFinalLitres(): number | null { return this._sourceExpectedFinalLitres }
    public set sourceExpectedFinalLitres(value: number | null) {
      this._sourceExpectedFinalLitres = value === undefined ? null : value;
    }
    private _targetExpectedFinalLitres: number | null = null;
    /**
    * The volume in the target vessel after the movement, if target is a vessel in the current assembly. Evaluated from the final litres (measured or expected) of the previous movement for the vessel and the TargetLitresArriving.
    */
    public get targetExpectedFinalLitres(): number | null { return this._targetExpectedFinalLitres }
    public set targetExpectedFinalLitres(value: number | null) {
      this._targetExpectedFinalLitres = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.vesselMovement = new VesselMovement(jsonSerializedObject.vesselMovement);
          this.sourceExpectedFinalLitres = jsonSerializedObject.sourceExpectedFinalLitres == null ? null : jsonSerializedObject.sourceExpectedFinalLitres;
          this.targetExpectedFinalLitres = jsonSerializedObject.targetExpectedFinalLitres == null ? null : jsonSerializedObject.targetExpectedFinalLitres;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.vesselMovement = this.vesselMovement.toJsonSerializedObject();
        toRet.sourceExpectedFinalLitres = this.sourceExpectedFinalLitres == null ? null : this.sourceExpectedFinalLitres;
        toRet.targetExpectedFinalLitres = this.targetExpectedFinalLitres == null ? null : this.targetExpectedFinalLitres;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "vesselMovement", rule: (v: any) => !!v || "vesselMovement is required"},
            {field: "vesselMovement", rule: (v: any) => v.validate()},
    ];
}


export class VesselMovementFilter extends ModelObject<VesselMovementFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _vesselIds: Guid[] | null = null;
    /**
    * Vessel to include
    */
    public get vesselIds(): Guid[] | null { return this._vesselIds }
    public set vesselIds(value: Guid[] | null) {
      this._vesselIds = value === undefined ? null : value;
    }
    private _fromMovementTimestamp: Instant | null = null;
    /**
    * Minimum (inclusive) movement timestamp
    */
    public get fromMovementTimestamp(): Instant | null { return this._fromMovementTimestamp }
    public set fromMovementTimestamp(value: Instant | null) {
      this._fromMovementTimestamp = value === undefined ? null : value;
    }
    private _toMovementTimestamp: Instant | null = null;
    /**
    * Maximum (inclusive) movement timestamp
    */
    public get toMovementTimestamp(): Instant | null { return this._toMovementTimestamp }
    public set toMovementTimestamp(value: Instant | null) {
      this._toMovementTimestamp = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.vesselIds = jsonSerializedObject.vesselIds == null ? null : jsonSerializedObject.vesselIds.map((v: any) => Guid.parseOrEmpty(v));
          this.fromMovementTimestamp = jsonSerializedObject.fromMovementTimestamp == null ? null : Instant.parse(jsonSerializedObject.fromMovementTimestamp);
          this.toMovementTimestamp = jsonSerializedObject.toMovementTimestamp == null ? null : Instant.parse(jsonSerializedObject.toMovementTimestamp);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.vesselIds = this.vesselIds == null ? null : this.vesselIds.map(v => v.toString());
        toRet.fromMovementTimestamp = this.fromMovementTimestamp == null ? null : this.fromMovementTimestamp.toString();
        toRet.toMovementTimestamp = this.toMovementTimestamp == null ? null : this.toMovementTimestamp.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class FilteredVesselMovementsResult extends ModelObject<FilteredVesselMovementsResult> {

    public matchingCount: number = 0;
    public vesselMovements: VesselMovement[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.vesselMovements = jsonSerializedObject.vesselMovements.map((v: any) => new VesselMovement(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.vesselMovements = this.vesselMovements.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "vesselMovements", rule: (v: any) => !!v || "vesselMovements is required"},
            {field: "vesselMovements", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as VesselMovement[]).reduce((acc: Array<string>, curr: VesselMovement) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class VesselContentVolume extends ModelObject<VesselContentVolume> {

    public vesselId: Guid = Guid.createEmpty();
    private _assemblyId: Guid | null = null;
    public get assemblyId(): Guid | null { return this._assemblyId }
    public set assemblyId(value: Guid | null) {
      this._assemblyId = value === undefined ? null : value;
    }
    private _processId: Guid | null = null;
    public get processId(): Guid | null { return this._processId }
    public set processId(value: Guid | null) {
      this._processId = value === undefined ? null : value;
    }
    public currentLitres: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.vesselId = Guid.parseOrEmpty(jsonSerializedObject.vesselId);
          this.assemblyId = jsonSerializedObject.assemblyId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.assemblyId);
          this.processId = jsonSerializedObject.processId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.processId);
          this.currentLitres = jsonSerializedObject.currentLitres;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.vesselId = this.vesselId.toString();
        toRet.assemblyId = this.assemblyId == null ? null : this.assemblyId.toString();
        toRet.processId = this.processId == null ? null : this.processId.toString();
        toRet.currentLitres = this.currentLitres;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "vesselId", rule: (v: any) => !v.isEmpty() || "VesselContentVolume.VesselId cannot be empty"},
            {field: "assemblyId", rule: (v: any) => v == null || !v.isEmpty() || "VesselContentVolume.AssemblyId cannot be empty"},
            {field: "processId", rule: (v: any) => v == null || !v.isEmpty() || "VesselContentVolume.ProcessId cannot be empty"},
    ];
}


export class VesselAssemblyCount extends ModelObject<VesselAssemblyCount> {

    public vesselId: Guid = Guid.createEmpty();
    /**
    * The number of different assemblies which the vessel has been filled within.
    */
    public countOfAssemblies: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.vesselId = Guid.parseOrEmpty(jsonSerializedObject.vesselId);
          this.countOfAssemblies = jsonSerializedObject.countOfAssemblies;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.vesselId = this.vesselId.toString();
        toRet.countOfAssemblies = this.countOfAssemblies;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "vesselId", rule: (v: any) => !v.isEmpty() || "VesselAssemblyCount.VesselId cannot be empty"},
            {field: "countOfAssemblies", rule: (v: any) => v == null || v >= -2147483648 || "CountOfAssemblies is not greater than minimum allowed value: -2147483648"},
            {field: "countOfAssemblies", rule: (v: any) => v == null || v <= 2147483647 || "CountOfAssemblies is above maximum allowed value: 2147483647"},
    ];
}


export class VesselProcessCount extends ModelObject<VesselProcessCount> {

    public vesselId: Guid = Guid.createEmpty();
    /**
    * The number of different processes which the vessel has been filled within.
    */
    public countOfProcess: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.vesselId = Guid.parseOrEmpty(jsonSerializedObject.vesselId);
          this.countOfProcess = jsonSerializedObject.countOfProcess;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.vesselId = this.vesselId.toString();
        toRet.countOfProcess = this.countOfProcess;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "vesselId", rule: (v: any) => !v.isEmpty() || "VesselProcessCount.VesselId cannot be empty"},
            {field: "countOfProcess", rule: (v: any) => v == null || v >= -2147483648 || "CountOfProcess is not greater than minimum allowed value: -2147483648"},
            {field: "countOfProcess", rule: (v: any) => v == null || v <= 2147483647 || "CountOfProcess is above maximum allowed value: 2147483647"},
    ];
}


export class VesselMovementService extends ServiceBase {

  public async getVesselMovementsForAssembly(tenantId: Guid, assemblyId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<VesselMovementWithFinal[]> {
    const req = {
    tenantId:tenantId.toString(),
    assemblyId:assemblyId.toString(),
    };
    const extractor = (response:any) => response.results.map((v: any) => new VesselMovementWithFinal(v));;
    return this.makeJsonRequest("vesselMovement/v1/getVesselMovementsForAssembly", req, false, extractor, opts);
  };

  public async saveVesselMovement(tenantId: Guid, vesselMovement: VesselMovement, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:tenantId.toString(),
    vesselMovement:vesselMovement.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("vesselMovement/v1/saveVesselMovement", req, true, extractor, opts);
  };

  public async getVesselMovementsByFilter(tenantId: Guid, filter: VesselMovementFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredVesselMovementsResult> {
    const req = {
    tenantId:tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredVesselMovementsResult(response.results);;
    return this.makeJsonRequest("vesselMovement/v1/getVesselMovementsByFilter", req, false, extractor, opts);
  };

  public async getContentVolumesOfAllVessels(tenantId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<VesselContentVolume[]> {
    const req = {
    tenantId:tenantId.toString(),
    };
    const extractor = (response:any) => response.vesselVolumes.map((v: any) => new VesselContentVolume(v));;
    return this.makeJsonRequest("vesselMovement/v1/getContentVolumesOfAllVessels", req, false, extractor, opts);
  };

  public async getContentVolumesOfVessels(tenantId: Guid, vesselIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<VesselContentVolume[]> {
    const req = {
    tenantId:tenantId.toString(),
    vesselIds:vesselIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.vesselVolumes.map((v: any) => new VesselContentVolume(v));;
    return this.makeJsonRequest("vesselMovement/v1/getContentVolumesOfVessels", req, false, extractor, opts);
  };

  public async getCountOfAssembliesForVessels(tenantId: Guid, vesselIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<VesselAssemblyCount[]> {
    const req = {
    tenantId:tenantId.toString(),
    vesselIds:vesselIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.counts.map((v: any) => new VesselAssemblyCount(v));;
    return this.makeJsonRequest("vesselMovement/v1/getCountOfAssembliesForVessels", req, false, extractor, opts);
  };

  public async getVesselMovementsForProcess(tenantId: Guid, processId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<VesselMovementWithFinal[]> {
    const req = {
    tenantId:tenantId.toString(),
    processId:processId.toString(),
    };
    const extractor = (response:any) => response.results.map((v: any) => new VesselMovementWithFinal(v));;
    return this.makeJsonRequest("vesselMovement/v1/getVesselMovementsForProcess", req, false, extractor, opts);
  };

  public async getCountOfProcessesForVessels(tenantId: Guid, vesselIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<VesselProcessCount[]> {
    const req = {
    tenantId:tenantId.toString(),
    vesselIds:vesselIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.counts.map((v: any) => new VesselProcessCount(v));;
    return this.makeJsonRequest("vesselMovement/v1/getCountOfProcessesForVessels", req, false, extractor, opts);
  };
}

export const vesselMovementService = new VesselMovementService();